// schema
import * as ISchema from "@schema";

// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createRefetchContainer } from "react-relay";
import { Disposable } from "relay-runtime";

// web-app imports
import { IContainer, IProps, IState } from "./WebApp.d";
import { webAppFragments, webAppQuery } from "./WebApp.gql";
import { styles } from "./WebApp.styles";

// components
import { WebAppOnboardingRenderer } from "@components/web-app-onboarding";
import { FlowStatePoller } from "@components/flow-state-poller";
import { WebAppChromeCard } from "@components/web-app-chrome-card";
import { CircularProgress } from "@clockwise/web-commons/src/components/circular-progress";
import { AutopilotListRenderer, AutopilotListMode } from "@components/autopilot-list";
import { AutopilotFeedStatusRenderer } from "@components/autopilot-feed-status";
import { SettingsDialog } from "@components/settings-dialog";
import { Event } from "@components/event";
import { WebAppOnboardingCompleteRenderer } from "@components/web-app-onboarding-complete";

// util
import { getCurrentOrg } from "@util/org.util";
import { getOnboardingFlowState, getFastCrawlFlowState } from "@state/onboarding";
import { logger } from "@util/logger.util";
import { track, TrackingEvents } from "@util/analytics.util";

// subscriptions
import { updateFlowStateSubscription } from "@subscriptions";

// state
import { IReduxState } from "@reducers/root.reducer";
import { setSettingsDialogOpen } from "@actions/chrome-extension.actions";
import {
  setOnboardingFlowState,
  setFastCrawlFlowState,
  setFastCrawlFinishedRefetchComplete,
  setFastCrawlTeammatesIdentifiedRefetchComplete,
  setFastCrawlUserCrawledRefetchComplete,
  TOnboardingFlowState,
  TFastCrawlFlowState,
} from "@actions/onboarding.actions";
import { toggleExpanded, sendInvite, openTeam } from "@actions/settings.actions";
import { ISettingsState } from "@reducers/settings.reducer";

// material-ui
import withStyles from "@material-ui/core/styles/withStyles";
import { withRouteQuery } from "@routing/routes.util";

///////////////
// Component //
///////////////
class WebAppCmpt extends React.Component<IProps, IState> {
  private fastCrawlDisposable?: Disposable;
  private didFinishRefetch: boolean = false;
  private didTeammatesRefetch: boolean = false;
  private didUserRefetch: boolean = false;
  private defaultEvent?: ISchema.IEvent;

  constructor(props: IProps) {
    super(props);
  }

  /////////////
  // Helpers //
  /////////////

  private goToEvent = (externalEventId: string) => {
    track(TrackingEvents.APP_HOME.AUTOPILOT_LIST_EVENT);
    this.props.router.push(`/app/event/${btoa(externalEventId)}`);
  };

  private maybeReRouteHome = (event?: ISchema.IEvent, isHome?: boolean) => {
    if (event && !window.matchMedia("(max-width: 768px)").matches && isHome) {
      const externalEventId = event.eventKey.externalEventId;

      this.goToEvent(externalEventId);
    }
  };

  //////////////
  // Handlers //
  //////////////

  private onClick = (externalEventId: string) => {
    this.goToEvent(externalEventId);
  };

  private onAutopilotListLoad = (event?: ISchema.IEvent) => {
    this.defaultEvent = event;

    this.maybeReRouteHome(event, this.props.match.location.pathname === "/app");
  };

  ///////////////
  // Lifecycle //
  ///////////////

  private unsubscribe = () => {
    if (this.fastCrawlDisposable) {
      this.fastCrawlDisposable.dispose();
      delete this.fastCrawlDisposable;
    }
  };

  private subscribe = (flowState: ISchema.IFlowState) => {
    this.unsubscribe();

    this.fastCrawlDisposable = updateFlowStateSubscription(this.props.relay.environment, {
      flowStateRelayId: flowState.id,
    });
  };

  private refetch = () => {
    this.props.relay.refetch({}, undefined, undefined, { force: true });
  };

  private maybeUpdateFastCrawlState = (fastCrawlFlowState: ISchema.IFlowState, error?: Error) => {
    if (error) {
      logger.error("Error polling flow state", error);
    }

    // only update the flow state on new state or new error
    if (
      fastCrawlFlowState.current.state !== this.props.fastCrawlFlowStateCurrentStatus ||
      fastCrawlFlowState.current.errorMessage !== this.props.fastCrawlFlowStateErrorMessage
    ) {
      this.props.setFastCrawlFlowState(fastCrawlFlowState as TFastCrawlFlowState);
    }

    // refetch if its needed (always check later statuses first to avoid multiple refetches)
    if (
      this.props.fastCrawlFinished &&
      !this.props.fastCrawlFinishedRefetchComplete &&
      !this.didFinishRefetch
    ) {
      // it would probably be more optimal to set this on complete of the refresh, but we dont
      // want to accidentally refetch again if this function gets called before we complete
      // also passing a callback to a refetch, might not result in the callback getting called!
      this.didFinishRefetch = true;
      this.props.setFastCrawlFinishedRefetchComplete();
      this.refetch();
    } else if (
      this.props.fastCrawlTeammatesIdentifiedComplete &&
      !this.props.fastCrawlTeammatesIdentifiedRefetchComplete &&
      !this.didTeammatesRefetch
    ) {
      this.didTeammatesRefetch = true;
      this.props.setFastCrawlTeammatesIdentifiedRefetchComplete();
      this.refetch();
    } else if (
      this.props.fastCrawlUserCrawledComplete &&
      !this.props.fastCrawlUserCrawledRefetchComplete &&
      !this.didUserRefetch
    ) {
      this.didUserRefetch = true;
      this.props.setFastCrawlUserCrawledRefetchComplete();
      this.refetch();
    }

    // start or stop subscription?
    if (
      this.fastCrawlDisposable &&
      this.props.fastCrawlFinished &&
      !this.props.fastCrawlFlowStateErrorMessage
    ) {
      this.unsubscribe();
      delete this.fastCrawlDisposable;
    } else if (
      !this.fastCrawlDisposable &&
      (!this.props.fastCrawlFinished || this.props.fastCrawlFlowStateErrorMessage)
    ) {
      this.subscribe(fastCrawlFlowState);
    }
  };

  private maybeUpdateFastCrawlStateFromProps = (nextProps: IProps) => {
    const org = getCurrentOrg(nextProps.viewer);
    const fastCrawlFlowState = org && getFastCrawlFlowState(org);
    if (!fastCrawlFlowState) {
      return;
    }

    this.maybeUpdateFastCrawlState(fastCrawlFlowState);
  };

  private maybeUpdateFlowStateFromProps = (nextProps: IProps) => {
    const onboardingFlowState =
      nextProps.viewer.user && getOnboardingFlowState(nextProps.viewer.user);
    if (!onboardingFlowState) {
      return;
    }

    // update state if diverging from current props
    if (onboardingFlowState.current.state !== this.props.onboardingFlowStateCurrentStatus) {
      this.props.setOnboardingFlowState(onboardingFlowState as TOnboardingFlowState);
    }
  };

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    this.maybeUpdateFastCrawlStateFromProps(nextProps);
    this.maybeUpdateFlowStateFromProps(nextProps);

    const onboardingState = this.props.viewer && getOnboardingFlowState(this.props.viewer.user);
    const nextOnboardingState = nextProps.viewer && getOnboardingFlowState(nextProps.viewer.user);

    if (this.props.match.location.pathname !== nextProps.match.location.pathname) {
      if (nextProps.match.location.pathname.startsWith("/app/settings")) {
        if (nextProps.match.params.section) {
          this.props.toggleExpanded(nextProps.match.params.section as keyof ISettingsState);
        }

        this.props.setSettingsDialogOpen(true);
      } else if (nextProps.match.location.pathname === "/app") {
        this.maybeReRouteHome(this.defaultEvent, true);
      }
    }

    if (
      onboardingState?.current.state !== nextOnboardingState?.current.state &&
      nextOnboardingState?.current.state === "Finished"
    ) {
      this.props.router.replace("/app");
    }
  }

  public UNSAFE_componentWillMount() {
    const onboardingState = this.props.viewer && getOnboardingFlowState(this.props.viewer.user);

    const onboardingFinished = onboardingState && onboardingState.current.state === "Finished";

    if (onboardingFinished && !this.props.match.location.pathname.startsWith("/app/event")) {
      this.props.router.replace("/app");
    }

    if (onboardingFinished && this.props.match.location.pathname.startsWith("/app/settings")) {
      if (this.props.match.params.section) {
        const section = this.props.match.params.section;
        switch (section) {
          case "defineteam":
            this.props.toggleExpanded("expandTeam");
            break;
          case "workinghours":
            if (this.props.match.params.subsection) {
              if (this.props.match.params.subsection === "meetingbreaks") {
                this.props.toggleExpanded("expandMeetingBreaks");
              }
            } else {
              this.props.toggleExpanded("expandHours");
            }
            break;
          case "meetingbreaks":
            this.props.toggleExpanded("expandMeetingBreaks");
            break;
          case "invite":
            const searchParams = new URLSearchParams(window.location.search);
            if (searchParams.has("email")) {
              const email = decodeURIComponent(searchParams.get("email") as string);
              const cleanEmail = email.split("?")[0]; // to guard against poorly formatted utm params getting added to the url
              this.props.sendInvite(cleanEmail);
            }
            this.props.toggleExpanded("expandInvites");
            break;
          case "email":
            this.props.toggleExpanded("expandEmailsAndNotifications");
            break;
          case "integrations":
          case "slack":
          case "asana":
          case "zoom":
            this.props.toggleExpanded("expandIntegrations");
            break;
          case "personalcal":
            this.props.toggleExpanded("expandPersonalCalendar");
            break;
          case "smartholds":
            this.props.toggleExpanded("expandSmartHolds");
            break;
          case "colors":
            this.props.toggleExpanded("expandColorCoding");
            break;
          case "focustime":
            this.props.toggleExpanded("expandFocusTime");
            break;
          case "traveltime":
            this.props.toggleExpanded("expandTravelTime");
            break;
          case "lunch":
            this.props.toggleExpanded("expandLunch");
            break;
          case "orgsettings":
          case "zoomadmin":
            this.props.toggleExpanded("expandOrgSettings");
            break;
          case "team":
            if (this.props.match.params.subsection) {
              const teamId =
                this.props.match.params.subsection === "primary"
                  ? ""
                  : atob(this.props.match.params.subsection);
              const feature = this.props.match.params.subsubsection;
              switch (feature) {
                case "manage":
                  this.props.openTeam(teamId, 2);
                  break;
                case "nomeetingday":
                  this.props.openTeam(teamId, 2, "nomeetingday");
                  break;
                default:
                  this.props.openTeam(teamId);
                  break;
              }
            } else {
              this.props.toggleExpanded("expandTeam");
            }
            break;
          default:
            break;
        }
      }

      this.props.setSettingsDialogOpen(true);

      this.props.router.push("/app");
    }

    if (!onboardingFinished) {
      this.props.router.push("/app/onboarding");
    }

    if (onboardingState) {
      this.subscribe(onboardingState);
    }
  }

  public componentWillUnmount() {
    this.unsubscribe();
  }

  ///////////////
  // Rendering //
  ///////////////

  private renderCurrentPage() {
    const { pathname } = this.props.match.location;

    if (pathname.startsWith("/app/event")) {
      const org = getCurrentOrg(this.props.viewer)!;

      try {
        const calendarId =
          this.props.match.params.encodedCalendarId &&
          this.props.match.params.encodedCalendarId !== "f"
            ? atob(this.props.match.params.encodedCalendarId)
            : org.primaryOrgCalendar;

        const externalEventId = atob(this.props.match.params.encodedExternalEventId);

        return (
          <div style={{ maxWidth: 750, width: "100%", margin: "0 auto" }}>
            <WebAppChromeCard viewer={this.props.viewer} />
            <div className={this.props.classes.event}>
              <Event
                withBorder={true}
                environment={this.props.relay.environment}
                externalEventId={externalEventId}
                calendarId={calendarId}
                org={org}
              />
            </div>
          </div>
        );
      } catch (e) {
        // do nothing here if we fail to decode, fallback to default case
      }
    }

    return (
      <div style={{ width: "100%", maxWidth: 750, margin: "0 auto" }}>
        <WebAppChromeCard viewer={this.props.viewer} />
      </div>
    );
  }

  public render() {
    const org = getCurrentOrg(this.props.viewer);

    if (!org) {
      return <CircularProgress />;
    }

    const orgTimeZone = org.orgMeetingHours.workingHours.timeZone;

    const onboardingState = this.props.viewer && getOnboardingFlowState(this.props.viewer.user);
    const fastCrawlFlowState = this.props.viewer && getFastCrawlFlowState(org);
    const alwaysShowOnboarding = this.props.match.location.pathname === "/app/onboarding-internal";

    if ((onboardingState && onboardingState.current.state !== "Finished") || alwaysShowOnboarding) {
      return (
        <div style={{ margin: "auto auto" }}>
          {fastCrawlFlowState ? (
            <FlowStatePoller
              flowState={fastCrawlFlowState}
              onFlowStateUpdate={this.maybeUpdateFastCrawlState}
            />
          ) : null}
          <WebAppOnboardingRenderer
            alwaysShow={alwaysShowOnboarding}
            orgRelayId={org.id}
            environment={this.props.relay.environment}
          />
        </div>
      );
    }

    if (this.props.match.location.pathname === "/app/onboarding") {
      return (
        <React.Fragment>
          <WebAppOnboardingCompleteRenderer
            environment={this.props.relay.environment}
            orgRelayId={org.id}
          />
          {this.props.settingsDialogOpen ? (
            <SettingsDialog
              environment={this.props.relay.environment}
              onClose={() => this.props.setSettingsDialogOpen(false)}
            />
          ) : null}
        </React.Fragment>
      );
    }

    const isHome = this.props.match.location.pathname === "/app";

    const hideSideBar =
      this.props.match.location.pathname.startsWith("/app/event") &&
      (this.props.match.params.fullView || this.props.match.params.encodedCalendarId === "f");

    let selectedExternalEventId: string | undefined = undefined;

    try {
      if (this.props.match.location.pathname.startsWith("/app/event")) {
        selectedExternalEventId = atob(this.props.match.params.encodedExternalEventId);
      }
    } catch (e) {
      // just do nothing if we fail to decode the event id
    }

    return (
      <React.Fragment>
        <div style={{ display: "flex", flexDirection: "row", width: "100%", height: "100%" }}>
          {!hideSideBar ? (
            <div
              className={this.props.classes.sideBarContainer}
              style={{ display: isHome ? "block" : undefined }}
            >
              <div className={this.props.classes.absoluteSideBarContainer}>
                <div style={{ padding: "0 10px", width: "100%" }}>
                  <AutopilotFeedStatusRenderer
                    isActionable={false}
                    environment={this.props.relay.environment}
                    orgRelayId={org.id}
                    orgTimeZone={orgTimeZone}
                    fastCrawlFinished={this.props.fastCrawlFinished}
                  />
                </div>
                <AutopilotListRenderer
                  showHistory={false}
                  selectedExternalEventId={selectedExternalEventId}
                  type={AutopilotListMode.Menu}
                  onClick={this.onClick}
                  onLoad={this.onAutopilotListLoad}
                  environment={this.props.relay.environment}
                  orgId={org.id}
                  calendarId={org.primaryOrgCalendar}
                />
              </div>
            </div>
          ) : null}
          <div
            className={this.props.classes.page}
            style={{ display: !isHome ? "block" : undefined }}
          >
            <div className={this.props.classes.pageContent}>{this.renderCurrentPage()}</div>
          </div>
        </div>
        {this.props.settingsDialogOpen ? (
          <SettingsDialog
            environment={this.props.relay.environment}
            onClose={() => this.props.setSettingsDialogOpen(false)}
          />
        ) : null}
      </React.Fragment>
    );
  }
}

/////////////////
// With Styles //
/////////////////
export const WebAppStyled = withStyles(styles)(WebAppCmpt);

///////////
// Redux //
///////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    onboardingFlowStateCurrentStatus: state.onboarding.onboardingFlowStateCurrentStatus,
    fastCrawlFinished: state.onboarding.fastCrawlFinished,
    fastCrawlFlowStateCurrentStatus: state.onboarding.fastCrawlFlowStateCurrentStatus,
    fastCrawlFlowStateErrorMessage: state.onboarding.fastCrawlFlowStateErrorMessage,
    fastCrawlFinishedRefetchComplete: state.onboarding.fastCrawlFinishedRefetchComplete,
    fastCrawlTeammatesIdentifiedRefetchComplete:
      state.onboarding.fastCrawlTeammatesIdentifiedRefetchComplete,
    fastCrawlTeammatesIdentifiedComplete: state.onboarding.fastCrawlTeammatesIdentifiedComplete,
    fastCrawlUserCrawledComplete: state.onboarding.fastCrawlUserCrawledComplete,
    fastCrawlUserCrawledRefetchComplete: state.onboarding.fastCrawlUserCrawledRefetchComplete,
    settingsDialogOpen: state.webExtension.settingsDialogOpen,
  };
}

const WebAppRedux = connect(mapStateToProps, {
  setOnboardingFlowState,
  setSettingsDialogOpen,
  toggleExpanded,
  setFastCrawlFlowState,
  setFastCrawlFinishedRefetchComplete,
  setFastCrawlTeammatesIdentifiedRefetchComplete,
  setFastCrawlUserCrawledRefetchComplete,
  sendInvite,
  openTeam,
})(WebAppStyled);

///////////
// Relay //
///////////
export const WebApp = createRefetchContainer<IContainer>(WebAppRedux, webAppFragments, webAppQuery);

export const WebAppPage = withRouteQuery(WebApp, webAppQuery);
